import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { Link } from 'react-router-dom';
import { Phone, Clock, MapPin, Navigation, Building, CheckCircle, Car } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import LocationReviewsSection from './components/LocationReviewsSection';

const Locations = () => {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);

  // Constants
  const EMERGENCY_PHONE = '(916) 256-3468';
  const OFFICE_ADDRESS = '901 H Street, Suite 103, Sacramento, CA 95814';

  // Fetch facilities from Supabase
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const { data, error } = await supabase
          ?.from('facilities')
          ?.select('*')
          ?.order('facility_name', { ascending: true });

        if (!error && data) {
          setFacilities(data);
        }
      } catch (error) {
        console.error('Failed to fetch facilities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFacilities();
  }, []);

  // Service areas with detailed information
  const serviceAreas = [
    {
      id: 'sacramento',
      name: 'Sacramento County',
      description: 'Our main office location with 24/7 immediate response for all Sacramento County facilities.',
      responseTime: '15-30 minutes',
      coverage: 'All Sacramento County jails and courthouses',
      facilities: [
        'Sacramento County Main Jail',
        'Rio Cosumnes Correctional Center',
        'Sacramento County Courthouse'
      ],
      features: [
        'Office location with parking',
        'Handicap accessible',
        'Professional meeting spaces',
        'Clean, welcoming environment'
      ]
    },
    {
      id: 'placer',
      name: 'Placer County',
      description: 'Comprehensive bail bond services for Placer County with rapid response to all detention facilities.',
      responseTime: '30-45 minutes',
      coverage: 'All Placer County facilities',
      facilities: [
        'Placer County Jail - Auburn',
        'South Placer Jail - Roseville',
        'Placer County Courthouse'
      ],
      features: [
        'Local agent expertise',
        '24/7 immediate response',
        'Payment plan options',
        'Professional service'
      ]
    },
    {
      id: 'eldorado',
      name: 'El Dorado County',
      description: 'Serving El Dorado County with experienced agents and quick response times for all facilities.',
      responseTime: '30-45 minutes',
      coverage: 'All El Dorado County facilities',
      facilities: [
        'El Dorado County Jail - Placerville',
        'South Lake Tahoe Jail',
        'El Dorado County Courthouse'
      ],
      features: [
        'Mountain area coverage',
        'Fast processing',
        'Local knowledge',
        'Flexible payment terms'
      ]
    },
    {
      id: 'yolo',
      name: 'Yolo County',
      description: 'Complete coverage for Yolo County with professional service and rapid response capabilities.',
      responseTime: '30-45 minutes',
      coverage: 'All Yolo County facilities',
      facilities: [
        'Yolo County Jail - Woodland',
        'Monroe Detention Center',
        'Yolo County Courthouse'
      ],
      features: [
        'Expert local agents',
        '24/7 availability',
        'Quick processing',
        'Payment assistance'
      ]
    },
    {
      id: 'yuba',
      name: 'Yuba County',
      description: 'Dedicated bail bond services for Yuba County with experienced professionals and swift response.',
      responseTime: '30-45 minutes',
      coverage: 'All Yuba County facilities',
      facilities: [
        'Yuba County Jail - Marysville',
        'Yuba County Courthouse',
        'Juvenile Detention Center'
      ],
      features: [
        'Local jurisdiction experts',
        'Immediate response 24/7',
        'Compassionate service',
        'Flexible options'
      ]
    },
    {
      id: 'sanjjoaquin',
      name: 'San Joaquin County',
      description: 'Complete coverage for San Joaquin County with rapid response and local expertise.',
      responseTime: '30-45 minutes',
      coverage: 'All San Joaquin County facilities',
      facilities: [
        'San Joaquin County Jail',
        'Honor Farm',
        'San Joaquin County Courthouse'
      ],
      features: [
        'Local agent coverage',
        'Immediate response 24/7',
        'Familiar with local procedures',
        'Transportation services'
      ]
    },
    {
      id: 'stanislaus',
      name: 'Stanislaus County',
      description: 'Serving Stanislaus County with the same compassionate service and fast response times.',
      responseTime: '30-45 minutes',
      coverage: 'All Stanislaus County facilities',
      facilities: [
        'Stanislaus County Public Safety Center',
        'Downtown Jail',
        'Stanislaus County Courthouse'
      ],
      features: [
        'Local jurisdiction knowledge',
        'All counties coverage',
        'Fast processing times',
        'Flexible payment options'
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Locations & Service Areas | Sacramento, Stockton, Modesto | Sailors Bail Bonds</title>
        <meta 
          name="description" 
          content="Sailors Bail Bonds serves Sacramento, Stockton, Modesto and all California counties. Office at 901 H Street, Suite 103, Sacramento, CA 95814. Available 24/7. Call (916) 256-3468." 
        />
        <meta 
          name="keywords" 
          content="bail bonds locations, Sacramento bail bonds, Stockton bail, Modesto bail bonds, California bail bond locations" 
        />
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />
        
        {/* Breadcrumbs */}
        <nav className="pt-32 pb-4 px-4 sm:px-6 lg:px-8 bg-white border-b" aria-label="Breadcrumb">
          <div className="max-w-7xl mx-auto">
            <ol className="flex items-center space-x-2 text-sm" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              <li>
                <Link to="/" className="text-[#007BFF] hover:text-blue-700 transition-colors">
                  Home
                </Link>
              </li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-900 font-semibold">Locations</li>
            </ol>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007BFF] via-blue-600 to-blue-700">
          <div className="max-w-7xl mx-auto text-center">
            <h2 
              className="text-5xl lg:text-6xl font-black text-white mb-6 leading-tight"
              style={{ fontFamily: 'Roboto Slab, serif' }}
            >
              We're Here, No Matter Where You Are
            </h2>
            <p 
              className="text-2xl text-blue-100 leading-relaxed max-w-4xl mx-auto"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Drop by our office at 901 H Street, Suite 103, Sacramento, CA 95814, or reach us at {EMERGENCY_PHONE}—we cover Stockton, Modesto, and more.
            </p>
          </div>
        </section>

        {/* Main Office Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            {/* Office Building Photo Showcase */}
            <div className="mb-16">
              <div className="text-center mb-8">
                <h3 
                  className="text-4xl font-black text-gray-900 mb-4"
                  style={{ fontFamily: 'Roboto Slab, serif' }}
                >
                  Our Sacramento Office
                </h3>
                <p 
                  className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto"
                  style={{ fontFamily: 'Open Sans, sans-serif' }}
                >
                  Professional office building located at 901 H Court Plaza in downtown Sacramento
                </p>
              </div>
              
              {/* Professional Office Building Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-[#007BFF]/20 max-w-5xl mx-auto">
                <img 
                  src="/assets/images/Image_10-29-25_at_6.08_AM-1764525441489.jpeg"
                  alt="Professional exterior photograph of 901 H Court Plaza office building - modern multi-story structure with glass windows, curved architectural features, white cream colored facade, surrounded by mature trees with professional building signage visible in urban Sacramento business district"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                  <div className="flex items-center justify-between text-white">
                    <div>
                      <p className="text-2xl font-black mb-1" style={{ fontFamily: 'Roboto Slab, serif' }}>
                        901 H Court Plaza
                      </p>
                      <p className="text-base opacity-90" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        Suite 103, Sacramento, CA 95814
                      </p>
                    </div>
                    <div className="bg-[#007BFF] px-4 py-2 rounded-full">
                      <p className="text-sm font-bold">24/7 Available</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Building Highlights */}
              <div className="grid md:grid-cols-4 gap-6 mt-8 max-w-5xl mx-auto">
                <div className="bg-gradient-to-br from-[#F8F9FA] to-blue-50 border-2 border-[#007BFF]/20 rounded-xl p-6 text-center">
                  <Building className="w-8 h-8 text-[#007BFF] mx-auto mb-3" />
                  <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Roboto Slab, serif' }}>
                    Professional Setting
                  </p>
                </div>
                <div className="bg-gradient-to-br from-[#F8F9FA] to-blue-50 border-2 border-[#007BFF]/20 rounded-xl p-6 text-center">
                  <Car className="w-8 h-8 text-[#28A745] mx-auto mb-3" />
                  <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Roboto Slab, serif' }}>
                    Ample Parking
                  </p>
                </div>
                <div className="bg-gradient-to-br from-[#F8F9FA] to-blue-50 border-2 border-[#007BFF]/20 rounded-xl p-6 text-center">
                  <MapPin className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                  <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Roboto Slab, serif' }}>
                    Downtown Location
                  </p>
                </div>
                <div className="bg-gradient-to-br from-[#F8F9FA] to-blue-50 border-2 border-[#007BFF]/20 rounded-xl p-6 text-center">
                  <CheckCircle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                  <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Roboto Slab, serif' }}>
                    Easy Access
                  </p>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Office Info */}
              <div className="space-y-8">
                <div>
                  <h4 
                    className="text-3xl font-black text-gray-900 mb-4"
                    style={{ fontFamily: 'Roboto Slab, serif' }}
                  >
                    Visit Us Today
                  </h4>
                  <p 
                    className="text-xl text-gray-700 leading-relaxed"
                    style={{ fontFamily: 'Open Sans, sans-serif' }}
                  >
                    Visit us at our professional office building in downtown Sacramento. We're here 24/7 with a clean, welcoming environment and kind staff ready to help.
                  </p>
                </div>

                {/* Contact Details */}
                <div className="bg-gradient-to-br from-[#F8F9FA] to-blue-50 border-2 border-[#007BFF]/20 rounded-2xl p-8 space-y-6">
                  <div className="flex items-center space-x-4">
                    <MapPin className="w-8 h-8 text-[#007BFF]" />
                    <div>
                      <p className="text-sm font-semibold text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        Address:
                      </p>
                      <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Roboto Slab, serif' }}>
                        {OFFICE_ADDRESS}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <Phone className="w-8 h-8 text-[#28A745]" />
                    <div>
                      <p className="text-sm font-semibold text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        Phone (24/7):
                      </p>
                      <a 
                        href={`tel:${EMERGENCY_PHONE?.replace(/\D/g, '')}`}
                        className="text-lg font-bold text-[#28A745] hover:text-green-700 transition-colors"
                        style={{ fontFamily: 'Roboto Slab, serif' }}
                      >
                        {EMERGENCY_PHONE}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <Clock className="w-8 h-8 text-purple-600" />
                    <div>
                      <p className="text-sm font-semibold text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        Hours:
                      </p>
                      <p className="text-lg font-bold text-purple-600" style={{ fontFamily: 'Roboto Slab, serif' }}>
                        24/7 - Always Available
                      </p>
                    </div>
                  </div>
                </div>

                {/* Office Features */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-[#28A745]" />
                    <span className="text-base text-gray-700" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Ample Parking
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-[#28A745]" />
                    <span className="text-base text-gray-700" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Handicap Access
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-[#28A745]" />
                    <span className="text-base text-gray-700" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Professional Space
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-[#28A745]" />
                    <span className="text-base text-gray-700" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Clean Environment
                    </span>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(OFFICE_ADDRESS)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#007BFF] text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg flex items-center justify-center space-x-2"
                  >
                    <Navigation className="w-6 h-6" />
                    <span>Get Directions</span>
                  </a>
                  <a 
                    href={`tel:${EMERGENCY_PHONE?.replace(/\D/g, '')}`}
                    className="bg-gradient-to-r from-[#28A745] to-green-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 shadow-lg flex items-center justify-center space-x-2"
                  >
                    <Phone className="w-6 h-6" />
                    <span>Call Now</span>
                  </a>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="bg-white border-4 border-[#007BFF]/20 rounded-2xl overflow-hidden shadow-2xl">
                <iframe
                  title="Sailors Bail Bonds Office Location"
                  src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(OFFICE_ADDRESS)}`}
                  width="100%"
                  height="500"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-[500px]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas Grid */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-[#F8F9FA]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h3 
                className="text-4xl font-black text-gray-900 mb-4"
                style={{ fontFamily: 'Roboto Slab, serif' }}
              >
                Complete Coverage Across California
              </h3>
              <p 
                className="text-xl text-gray-600"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                No matter where your loved one is held, we're here to help
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {serviceAreas?.map((area) => (
                <div 
                  key={area?.id}
                  className="bg-white border-2 border-[#007BFF]/20 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all"
                >
                  {/* Area Header */}
                  <div className="flex items-center justify-between mb-6">
                    <h4 
                      className="text-3xl font-black text-gray-900"
                      style={{ fontFamily: 'Roboto Slab, serif' }}
                    >
                      {area?.name}
                    </h4>
                    <MapPin className="w-10 h-10 text-[#007BFF]" />
                  </div>

                  {/* Description */}
                  <p 
                    className="text-base text-gray-700 leading-relaxed mb-6"
                    style={{ fontFamily: 'Open Sans, sans-serif' }}
                  >
                    {area?.description}
                  </p>

                  {/* Response Time */}
                  <div className="bg-gradient-to-r from-[#28A745] to-green-600 text-white px-6 py-3 rounded-xl mb-6">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">Response Time:</span>
                      <span className="text-lg font-black">{area?.responseTime}</span>
                    </div>
                  </div>

                  {/* Coverage */}
                  <div className="mb-6">
                    <p className="text-sm font-semibold text-gray-600 mb-2" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Coverage:
                    </p>
                    <p className="text-base font-bold text-[#007BFF]" style={{ fontFamily: 'Roboto Slab, serif' }}>
                      {area?.coverage}
                    </p>
                  </div>

                  {/* Facilities */}
                  <div className="mb-6">
                    <p className="text-sm font-semibold text-gray-600 mb-3" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Facilities Served:
                    </p>
                    <ul className="space-y-2">
                      {area?.facilities?.map((facility, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <Building className="w-4 h-4 text-[#007BFF] mt-1 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{facility}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Features */}
                  <div className="mb-6">
                    <p className="text-sm font-semibold text-gray-600 mb-3" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      Service Features:
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {area?.features?.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-[#28A745]" />
                          <span className="text-xs text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Facilities List from Database */}
        {!loading && facilities?.length > 0 && (
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h3 
                  className="text-4xl font-black text-gray-900 mb-4"
                  style={{ fontFamily: 'Roboto Slab, serif' }}
                >
                  All Covered Facilities
                </h3>
                <p 
                  className="text-xl text-gray-600"
                  style={{ fontFamily: 'Open Sans, sans-serif' }}
                >
                  Complete list of jails and courthouses we serve
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {facilities?.map((facility) => (
                  <div 
                    key={facility?.facility_id}
                    className="bg-gradient-to-br from-[#F8F9FA] to-blue-50 border-2 border-[#007BFF]/20 rounded-xl p-6 hover:shadow-lg transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      <Building className="w-6 h-6 text-[#007BFF]" />
                      <p className="text-base font-bold text-gray-900" style={{ fontFamily: 'Roboto Slab, serif' }}>
                        {facility?.facility_name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* NEW: Location Reviews Section with Pagination and Search */}
        <LocationReviewsSection />

        {/* Quick Contact CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#007BFF] to-blue-600">
          <div className="max-w-5xl mx-auto text-center">
            <h3 
              className="text-4xl font-black text-white mb-6"
              style={{ fontFamily: 'Roboto Slab, serif' }}
            >
              Need Help Right Now?
            </h3>
            <p 
              className="text-2xl text-blue-100 mb-8 leading-relaxed"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              Call us immediately or visit our office—we're ready to help 24/7
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a 
                href={`tel:${EMERGENCY_PHONE?.replace(/\D/g, '')}`}
                className="group bg-gradient-to-r from-[#28A745] to-green-600 text-white px-10 py-6 rounded-2xl font-black text-2xl hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center space-x-3 border-4 border-green-400"
              >
                <Phone className="w-8 h-8 group-hover:animate-pulse" />
                <span>Call: {EMERGENCY_PHONE}</span>
              </a>

              <a 
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(OFFICE_ADDRESS)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-[#007BFF] px-10 py-6 rounded-2xl font-black text-2xl hover:bg-gray-100 transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center space-x-3"
              >
                <Car className="w-8 h-8" />
                <span>Visit Office</span>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Locations;